import React, { useState, useEffect } from "react";
import $ from "jquery";
import {
  X,
  Accessibility as AccessibilityIcon,
  AArrowUp,
  AArrowDown,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Contrast,
  Palette,
  Underline,
  Bold,
} from "lucide-react";

const pluses = /\+/g;
const config = {};

const Accessibility = () => {
  const [accessibilityOpen, setAccessibilityOpen] = useState(false);
  const [linksUnderlined, setLinksUnderlined] = useState(false);
  const [linksBolded, setLinksBolded] = useState(false);
  const [colorMode, setColorMode] = useState(null);

  useEffect(() => {
    onLoadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (linksUnderlined) {
      $("body").addClass("links-underlined");
    } else {
      $("body").removeClass("links-underlined");
    }
  }, [linksUnderlined]);

  useEffect(() => {
    if (linksBolded) {
      $("body").addClass("links-bolded");
    } else {
      $("body").removeClass("links-bolded");
    }
  }, [linksBolded]);

  const setCookie = (key, value) => {
    if (value !== undefined) {
      const options = $.extend({}, config.defaults);
      if (typeof options.expires === "number") {
        const days = options.expires,
          t = (options.expires = new Date());
        t.setTime(+t + days * 864e5);
      }
      return (document.cookie = [
        encodeURIComponent(key),
        "=",
        config.raw ? value : encodeURIComponent(value),
        options.expires ? "; expires=" + options.expires.toUTCString() : "",
        options.path ? "; path=" + options.path : "",
        options.domain ? "; domain=" + options.domain : "",
        options.secure ? "; secure" : "",
      ].join(""));
    }

    let result = key ? undefined : {};
    let cookies = document.cookie ? document.cookie.split("; ") : [];
    for (let i = 0; i < cookies.length; i++) {
      let parts = cookies[i].split("=");
      let name = decode(parts.shift());
      let cookie = parts.join("=");
      if (key === name) {
        result = read(cookie);
        break;
      }
      if (!key && (cookie = read(cookie)) !== undefined) {
        result[name] = cookie;
      }
    }
    return result;
  };

  const removeCookie = (key) => {
    setCookie(key, "", { expires: -1, path: "/" });
    return !setCookie(key);
  };

  const decode = (s) => {
    return config.raw ? s : decodeURIComponent(s);
  };

  const parseCookieValue = (s) => {
    if (s.indexOf('"') === 0) {
      s = s.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, "\\");
    }
    try {
      s = decodeURIComponent(s.replace(pluses, " "));
      return config.json ? JSON.parse(s) : s;
    } catch {
      /* ignore malformed cookies */
    }
  };

  const read = (s, converter) => {
    const value = config.raw ? s : parseCookieValue(s);
    return $.isFunction(converter) ? converter(value) : value;
  };

  const openAccessibility = () => {
    if (accessibilityOpen) {
      $(".accessibility").slideUp(180);
    } else {
      $(".accessibility").slideDown(180);
    }
    setAccessibilityOpen(!accessibilityOpen);
  };

  const onLoadPage = () => {
    const savedBodyClass = setCookie("bodyClass");
    if (savedBodyClass) {
      $("body").addClass(savedBodyClass);
    }

    const savedZoom = setCookie("bodyZoom");
    if (savedZoom) {
      $("body").addClass(savedZoom);
    }

    const savedGrayscale = setCookie("bodyGrayscale");
    if (savedGrayscale) {
      document.documentElement.classList.add("grayscale_body");
      setColorMode("grayscale");
    }

    const savedInvert = setCookie("bodyInvert");
    if (savedInvert) {
      document.documentElement.classList.add("invert_body");
      setColorMode("invert");
    }

    const savedLinks = setCookie("bodyLinks");
    if (savedLinks) {
      setLinksUnderlined(true);
    }

    const savedBold = setCookie("bodyLinksBold");
    if (savedBold) {
      setLinksBolded(true);
    }

    applyFontSizeScaling();
  };

  const decrease_links = () => {
    setLinksBolded(true);
    setCookie("bodyLinksBold", "links_bolded");
  };

  const increase_links = () => {
    setLinksUnderlined(true);
    setCookie("bodyLinks", "links-underlined");
  };

  const increase_fs = () => {
    const body = $("body");
    if (body.hasClass("sizeL")) {
      body.removeClass("sizeL").addClass("sizeXL");
      setCookie("bodyClass", "sizeXL");
    } else if (body.hasClass("sizeXL")) {
      body.removeClass("sizeXL").addClass("sizeXXL");
      setCookie("bodyClass", "sizeXXL");
    } else if (body.hasClass("sizeXXL")) {
      body.removeClass("sizeXXL").addClass("sizeXXXL");
      setCookie("bodyClass", "sizeXXXL");
    } else {
      body.addClass("sizeL");
      setCookie("bodyClass", "sizeL");
    }
    applyFontSizeScaling();
  };

  const decrease_fs = () => {
    const body = $("body");
    if (body.hasClass("sizeXXXL")) {
      body.removeClass("sizeXXXL").addClass("sizeXXL");
      setCookie("bodyClass", "sizeXXL");
    } else if (body.hasClass("sizeXXL")) {
      body.removeClass("sizeXXL").addClass("sizeXL");
      setCookie("bodyClass", "sizeXL");
    } else if (body.hasClass("sizeXL")) {
      body.removeClass("sizeXL").addClass("sizeL");
      setCookie("bodyClass", "sizeL");
    } else if (body.hasClass("sizeL")) {
      body.removeClass("sizeL");
      removeCookie("bodyClass");
    }
    applyFontSizeScaling();
  };

  const applyFontSizeScaling = () => {
    const body = $("body");
    let scale = 1;

    if (body.hasClass("sizeL")) scale = 1.07;
    else if (body.hasClass("sizeXL")) scale = 1.14;
    else if (body.hasClass("sizeXXL")) scale = 1.21;
    else if (body.hasClass("sizeXXXL")) scale = 1.28;

    $("*")
      .not(
        `
        .accessibility-wrapper, .accessibility-wrapper *, 
        .header-wrapper, .header-wrapper *,
        .smart-scroll-row, .smart-scroll-row *,
        nav, nav *, footer, footer *
    `,
      )
      .each(function () {
        const element = $(this);

        if (!element.data("originalFontSize")) {
          const computedStyle = window.getComputedStyle(this);
          const originalSize = parseFloat(computedStyle.fontSize);
          if (originalSize) {
            element.data("originalFontSize", originalSize);
          }
        }

        const originalSize = element.data("originalFontSize");
        if (originalSize) {
          const newSize = originalSize * scale;
          element.css("font-size", newSize + "px");
        }
      });
  };

  const invert_body = () => {
    restore_color_body();
    document.documentElement.classList.add("invert_body");
    setCookie("bodyInvert", "invert_body");
    setColorMode("invert");
  };

  const grayscale_body = () => {
    restore_color_body();
    document.documentElement.classList.add("grayscale_body");
    setCookie("bodyGrayscale", "grayscale_body");
    setColorMode("grayscale");
  };

  const restore_color_body = () => {
    document.documentElement.classList.remove("grayscale_body", "invert_body");
    $("body").removeClass("grayscale_body invert_body");
    removeCookie("bodyGrayscale");
    removeCookie("bodyInvert");
    setColorMode(null);
  };

  const restore_fs = () => {
    $("body").removeClass("sizeL sizeXL sizeXXL sizeXXXL");
    removeCookie("bodyClass");
    $("*").css("font-size", "");
    applyFontSizeScaling();
  };

  const zoom_in_body = () => {
    if ($("body").hasClass("zoomL")) {
      $("body").removeClass("zoomL").addClass("zoomXL");
      setCookie("bodyZoom", "zoomXL");
    } else if ($("body").hasClass("zoomXL")) {
      $("body").removeClass("zoomXL").addClass("zoomXXL");
      setCookie("bodyZoom", "zoomXXL");
    } else {
      $("body").addClass("zoomL");
      setCookie("bodyZoom", "zoomL");
    }
  };

  const zoom_out_body = () => {
    if ($("body").hasClass("zoomXXL")) {
      $("body").removeClass("zoomXXL").addClass("zoomXL");
      removeCookie("bodyZoom");
      setCookie("bodyZoom", "zoomXL");
    } else if ($("body").hasClass("zoomXL")) {
      $("body").removeClass("zoomXL").addClass("zoomL");
      removeCookie("bodyZoom");
      setCookie("bodyZoom", "zoomL");
    } else if ($("body").hasClass("zoomL")) {
      $("body").removeClass("zoomL");
      removeCookie("bodyZoom");
    }
  };

  const restore_zoom_body = () => {
    if ($("body").hasClass("zoomXXL")) {
      $("body").removeClass("zoomXXL");
    }
    if ($("body").hasClass("zoomXL")) {
      $("body").removeClass("zoomXL");
    }
    if ($("body").hasClass("zoomL")) {
      $("body").removeClass("zoomL");
    }
    removeCookie("bodyZoom");
  };

  const restore_links = () => {
    setLinksUnderlined(false);
    setLinksBolded(false);
    removeCookie("bodyLinks");
    removeCookie("bodyLinksBold");
  };

  const sections = [
    {
      id: "font",
      title: "גודל הגופן",
      actions: [
        {
          id: "font-up",
          label: "הגדל גופן",
          icon: AArrowUp,
          onClick: increase_fs,
        },
        {
          id: "font-down",
          label: "הקטן גופן",
          icon: AArrowDown,
          onClick: decrease_fs,
        },
        {
          id: "font-reset",
          label: "שחזר גודל",
          icon: RotateCcw,
          onClick: restore_fs,
          isReset: true,
        },
      ],
    },
    {
      id: "zoom",
      title: "תצוגה",
      actions: [
        {
          id: "zoom-in",
          label: "הגדל תצוגה",
          icon: ZoomIn,
          onClick: zoom_in_body,
        },
        {
          id: "zoom-out",
          label: "הקטן תצוגה",
          icon: ZoomOut,
          onClick: zoom_out_body,
        },
        {
          id: "zoom-reset",
          label: "שחזר תצוגה",
          icon: RotateCcw,
          onClick: restore_zoom_body,
          isReset: true,
        },
      ],
    },
    {
      id: "color",
      title: "צבעוניות",
      actions: [
        {
          id: "grayscale",
          label: "שחור לבן",
          icon: Contrast,
          onClick: grayscale_body,
          active: colorMode === "grayscale",
        },
        {
          id: "invert",
          label: "הפוך צבעים",
          icon: Palette,
          onClick: invert_body,
          active: colorMode === "invert",
        },
        {
          id: "color-reset",
          label: "שחזר צבעים",
          icon: RotateCcw,
          onClick: restore_color_body,
          isReset: true,
        },
      ],
    },
    {
      id: "links",
      title: "לינקים",
      actions: [
        {
          id: "underline",
          label: "קו מתחת",
          icon: Underline,
          onClick: increase_links,
          active: linksUnderlined,
        },
        {
          id: "bold",
          label: "הדגש לינקים",
          icon: Bold,
          onClick: decrease_links,
          active: linksBolded,
        },
        {
          id: "links-reset",
          label: "שחזר לינקים",
          icon: RotateCcw,
          onClick: restore_links,
          isReset: true,
        },
      ],
    },
  ];

  return (
    <>
      <button
        type="button"
        className="nav-pill-btn nav-pill-btn--icon-only"
        onClick={openAccessibility}
        aria-label="נגישות"
        aria-expanded={accessibilityOpen}
        aria-controls="accessibility-panel"
      >
        <AccessibilityIcon size={18} strokeWidth={2} aria-hidden />
        <span>נגישות</span>
      </button>

      <div className="accessibility-wrapper">
        <div
          id="accessibility-panel"
          className="accessibility"
          role="dialog"
          aria-label="הגדרות נגישות"
          aria-hidden={!accessibilityOpen}
        >
          <div className="accessibility__header">
            <div className="accessibility__heading">
              <AccessibilityIcon size={20} strokeWidth={2.2} aria-hidden />
              <h2 className="accessibility__title">הגדרות נגישות</h2>
            </div>
            <button
              type="button"
              className="accessibility__close"
              onClick={openAccessibility}
              aria-label="סגור הגדרות נגישות"
            >
              <X size={18} strokeWidth={2.2} aria-hidden />
            </button>
          </div>

          <div className="accessibility__body">
            {sections.map((section) => (
              <section
                key={section.id}
                className="accessibility__section"
                aria-labelledby={`a11y-${section.id}-title`}
              >
                <h3
                  id={`a11y-${section.id}-title`}
                  className="accessibility__section-title"
                >
                  {section.title}
                </h3>
                <div className="accessibility__actions">
                  {section.actions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.id}
                        type="button"
                        className={[
                          "accessibility__action",
                          action.active ? "is-active" : "",
                          action.isReset ? "is-reset" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={action.onClick}
                        aria-pressed={
                          action.active != null ? !!action.active : undefined
                        }
                      >
                        <span
                          className="accessibility__action-icon"
                          aria-hidden
                        >
                          <Icon size={18} strokeWidth={2.1} />
                        </span>
                        <span className="accessibility__action-label">
                          {action.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Accessibility;
